import {
  ArrowDownTrayIcon,
  CircleStackIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import { Row } from "@tanstack/react-table";
import { format, parseISO } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import {
  AnalysisCell,
  ColumnOneTableCellSkeleton,
  MultipleReportsModal,
  PageNotFound,
  PlayerDetailsHeaderSkeleton,
  SessionErrorModal,
  Table,
  TableCellSkeleton,
} from "../../../components";
import {
  columnHelper,
  SharedSessionColumns,
} from "../../../components/shared/SharedSessionColumns";
import { useModalContext } from "../../../contexts/ModalContext";
import { usePlayerContext } from "../../../contexts/PlayerContext";
import { useUIContext } from "../../../contexts/UIContext";
import { useVisualizerContext } from "../../../contexts/VisualizerContext";
import { DataExportModal } from "../../../features";
import { buildMovementTypeOptions } from "../../../features/DataVisualizer/utils/visualizerHelpers";
import { usePagination } from "../../../hooks/usePagination";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { dataExportSlice } from "../../../redux/slices/dataExportSlice";
import { playerSlice } from "../../../redux/slices/playerSlice";
import {
  useGetPlayerByIdQuery,
  useGetReportsByOrgPlayerIdQuery,
  useGetSessionsByPlayerIdQuery,
} from "../../../services";
import {
  sessionListLimit,
  useSessionsTableFilters,
} from "../../../shared/Constants";
import {
  parseCompletedSessions,
  parseInProgressSessions,
  processNumMovements,
  showPlayerHandedness,
  toTitleCase,
  upsertPlayerById,
} from "../../../shared/Functions";
import { ReportType, Session } from "../../../types";
import { PlayerDetailsHeader } from "./PlayerDetailsHeader";

type PlayerRouteParams = {
  playerId: string;
};

export const PlayerDetails: React.FC = (): JSX.Element => {
  const playerRouteParams = useParams<PlayerRouteParams>();
  const orgPlayerId = playerRouteParams.playerId;

  // State and AppContext
  const [selectedPageSize, setSelectedPageSize] = useState(
    sessionStorage.getItem("sessionPageSize") || "10",
  );
  const { setModalState } = useModalContext();
  const { playerIdToRetrieve } = usePlayerContext();
  const { setDisplayBack } = useUIContext();
  const { setVisualizerInfo, setIsVisualizerModalOpen } =
    useVisualizerContext();
  const reportsLimit = 100;

  // Redux calls
  const playerList = useAppSelector((state) => state.players.playerList);
  const orgType = useAppSelector((state) => state.org.type);
  const movementTypeSlugs = useAppSelector(
    (state) => state.org,
  ).details.movementTypes.map((type: any) => type.slug);
  const dataExportState = useAppSelector((state) => state.dataExport);

  // Table filters
  const sessionsTableFilters = useSessionsTableFilters();

  // State for pagination
  const [offsetForSessions, setOffsetForSessions] = useState(0);
  const [offsetForReports, setOffsetForReports] = useState(0);
  const [errorModalSession, setErrorModalSession] = useState<Session | null>(
    null,
  );

  // RTK slice actions
  const dispatch = useAppDispatch();
  const { storePlayers } = playerSlice.actions;
  const { storeDataExport, clearDataExport } = dataExportSlice.actions;

  // RTK Queries
  const { data: playerDetails, isFetching: fetchingPlayerDetails } =
    useGetPlayerByIdQuery(orgPlayerId!);
  const sessionsQuery = useGetSessionsByPlayerIdQuery({
    orgPlayerIds: [orgPlayerId!],
    offset: offsetForSessions,
    limit: sessionListLimit,
  });
  const reportsQuery = useGetReportsByOrgPlayerIdQuery({
    orgPlayerId,
    offset: offsetForReports,
    limit: reportsLimit,
    movementTypes: movementTypeSlugs,
  });
  const { data: editedPlayer } = useGetPlayerByIdQuery(playerIdToRetrieve, {
    skip: !playerIdToRetrieve,
  });

  // Use pagination hook for sessions
  const { allData: playerSessions, fetching: fetchingSessionsStable } =
    usePagination(
      sessionsQuery,
      sessionListLimit,
      offsetForSessions,
      setOffsetForSessions,
      (session: Session) => session.id,
    );

  // Use pagination hook for reports
  const { allData: playerReports, fetching: fetchingReportsStable } =
    usePagination(
      reportsQuery,
      reportsLimit,
      offsetForReports,
      setOffsetForReports,
      (report: ReportType) => report.reportUrl,
    );

  // Handle edited player updates
  useEffect(() => {
    if (editedPlayer) {
      const updatedPlayerList = upsertPlayerById(playerList, editedPlayer);
      dispatch(storePlayers(updatedPlayerList));

      window.history.replaceState(
        {},
        "",
        `/player/${editedPlayer.orgPlayerId}`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedPlayer, playerIdToRetrieve, dispatch, storePlayers]);

  const exportDataDropdownOptions = [
    { name: "Export CSV", value: "csv", Icon: TableCellsIcon },
    { name: "Export Parquet", value: "parquet", Icon: CircleStackIcon },
  ];

  const memoizedSharedSessionColumns = useMemo(
    () => SharedSessionColumns(true),
    [],
  );

  const playerDetailColumns = useMemo(
    () => [
      columnHelper.accessor("checkbox", {
        header: "",
        enableSorting: false,
        size: 20,
        cell: function checkboxCell(element: any) {
          const session = element.row.original;
          if (session.status === "in-progress") {
            return null;
          } else {
            return (
              <input
                type="checkbox"
                className="border-gray-300 rounded-[4px] mr-4 checked:bg-[#FB6A07] checked:focus:bg-[#FB6A07] checked:hover:bg-[#FB6A07] focus:ring-[#FB6A07] focus:ring-opacity-50 focus:ring-2"
                data-testid="session-checkbox"
                defaultChecked={dataExportState.sessionList.some(
                  (s: any) => s.id === session.id,
                )}
                onChange={() => handleCheckboxChange(session)}
              />
            );
          }
        },
      }),
      ...memoizedSharedSessionColumns,
      columnHelper.accessor("hand", {
        header: "Hand",
        sortingFn: (rowA: Row<any>, rowB: Row<any>) => {
          const handsRowA = rowA.original.hand.split(", ");
          const handsRowB = rowB.original.hand.split(", ");
          if (handsRowA[0] > handsRowB[0]) {
            return 1;
          }
          if (handsRowB[0] > handsRowA[0]) {
            return -1;
          }
          return 0;
        },
        sortDescFirst: false,
        size: 150,
        cell: function handCell(element: any) {
          return <p data-testid="hand-cell">{element.getValue()}</p>;
        },
      }),
      columnHelper.accessor("analysis", {
        header: "Actions",
        enableSorting: false,
        cell: function analysisCell(element: any) {
          const session = element.row.original;
          const movementTypes = Object.keys(
            element.row.original.totalMovements,
          );

          const { movementTypeOptions, singleMovementHandler, canVisualize } =
            buildMovementTypeOptions(
              movementTypes,
              playerDetails,
              "player",
              setVisualizerInfo,
              setIsVisualizerModalOpen,
            );

          return (
            <AnalysisCell
              element={element}
              fetchingReports={fetchingReportsStable}
              session={session}
              openReportsModal={viewPlayerReports}
              showVisualizeButton={canVisualize}
              handleVisualizeButton={singleMovementHandler}
              visualizeButtonData={session}
              movementTypeOptions={
                movementTypeOptions.length > 1 ? movementTypeOptions : undefined
              }
            />
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dataExportState.sessionList,
      fetchingReportsStable,
      memoizedSharedSessionColumns,
    ],
  );

  const columnsWithoutHand = useMemo(
    () => playerDetailColumns.filter((column) => column.accessorKey !== "hand"),
    [playerDetailColumns],
  );

  const handleErrorClick = useCallback((session: Session) => {
    setErrorModalSession(session);
  }, []);

  const playerCompletedSessions: Session[] = useMemo(() => {
    return parseCompletedSessions(
      playerSessions?.filter((session) => {
        return session.percentComplete > 99;
      }) || [],
      playerReports,
      handleErrorClick,
    );
  }, [playerSessions, playerReports, handleErrorClick]);

  const playerProcessingSessions: Session[] = useMemo(() => {
    return parseInProgressSessions(
      playerSessions?.filter((session) => {
        return session.percentComplete <= 99;
      }) || [],
      playerReports,
    );
  }, [playerSessions, playerReports]);

  const finalPlayerSessions: Session[] = useMemo(() => {
    return [...playerCompletedSessions, ...playerProcessingSessions].sort(
      (a: Session, b: Session): number => {
        return a.sessionDate > b.sessionDate ? -1 : 1;
      },
    );
  }, [playerCompletedSessions, playerProcessingSessions]);

  const tableData = useMemo(
    () => (fetchingSessionsStable ? Array(10).fill({}) : finalPlayerSessions),
    [fetchingSessionsStable, finalPlayerSessions],
  );

  const tableColumns = useMemo(
    () =>
      fetchingSessionsStable
        ? (!showPlayerHandedness(orgType)
            ? columnsWithoutHand
            : playerDetailColumns
          ).map((column, index) => ({
            ...column,
            cell:
              index === 0 ? (
                <ColumnOneTableCellSkeleton />
              ) : (
                <TableCellSkeleton />
              ),
          }))
        : !showPlayerHandedness(orgType)
          ? columnsWithoutHand
          : playerDetailColumns,
    [columnsWithoutHand, fetchingSessionsStable, orgType, playerDetailColumns],
  );

  const viewPlayerReports = (session: any) => {
    const numberOfMovements = processNumMovements(
      session.totalMovements,
      session.sessionType.slug,
      true,
    );
    setModalState((prev) => ({
      ...prev,
      shouldOpen: true,
      title: `${format(parseISO(session.sessionDate), "M/d/yyyy")} - ${
        session.sessionType.name
      } - ${numberOfMovements}`,
      singleStep: <MultipleReportsModal reports={session.reports} />,
    }));
  };

  const openExportDataModal = (fileType: string) => {
    dispatch(
      storeDataExport({
        ...dataExportState,
        playerName: playerDetails?.firstName + " " + playerDetails?.lastName,
        orgPlayerId: orgPlayerId,
        dataFormat: fileType,
      }),
    );
    setModalState((prev) => ({
      ...prev,
      shouldOpen: true,
      title: `Define Export - ${
        fileType === "csv" ? fileType.toUpperCase() : toTitleCase(fileType)
      }`,
      singleStep: <DataExportModal />,
    }));
  };

  const handleCheckboxChange = (session: Session) => {
    const checkedSession = {
      id: session.id,
      date: format(new Date(session.sessionDate), "M/d/yyyy"),
    };

    if (dataExportState.sessionList.some((s: any) => s.id === session.id)) {
      const updatedSelectedSessions = dataExportState.sessionList.filter(
        (s: any) => s.id !== session.id,
      );
      dispatch(
        storeDataExport({
          ...dataExportState,
          sessionList: updatedSelectedSessions,
        }),
      );
    } else {
      dispatch(
        storeDataExport({
          ...dataExportState,
          sessionList: [...dataExportState.sessionList, checkedSession],
        }),
      );
    }
  };

  useEffect(() => {
    setDisplayBack && setDisplayBack(true);
  }, [setDisplayBack]);

  useEffect(() => {
    dispatch(clearDataExport());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SessionErrorModal
        isOpen={errorModalSession !== null}
        onClose={() => setErrorModalSession(null)}
        session={errorModalSession}
      />
      {!fetchingPlayerDetails && !playerDetails ? (
        <PageNotFound />
      ) : (
        <main className="relative pb-20 z-0 overflow-y-auto w-full">
          <div className="" data-testid="header-container">
            {fetchingPlayerDetails ? (
              <PlayerDetailsHeaderSkeleton orgType={orgType} />
            ) : playerDetails ? (
              <PlayerDetailsHeader player={playerDetails} />
            ) : null}
          </div>
          <div className="flex justify-start mt-8">
            <h2
              className="max-w-7xl mt-4 px-4 text-3xl leading-6 font-medium text-gray-900 sm:px-6 lg:px-12 lg:pb-6"
              data-testid="player-sessions-header"
            >
              Sessions
            </h2>
          </div>
          <div className="px-4" data-testid="player-sessions-table">
            <Table
              columns={tableColumns}
              data={tableData}
              selectedPageSize={selectedPageSize}
              setSelectedPageSize={setSelectedPageSize}
              displayFooter={true}
              fetching={fetchingSessionsStable}
              customRowClassName="player-details-row"
              component="playerDetails"
              buttons={[
                {
                  type: "dropdown",
                  action: openExportDataModal,
                  text: "Export Data",
                  dataTestid: "export-data-dropdown-button",
                  Icon: ArrowDownTrayIcon,
                  dropdownList: exportDataDropdownOptions,
                },
              ]}
              disableButton={dataExportState.sessionList.length === 0}
              filters={sessionsTableFilters}
            />
          </div>
        </main>
      )}
    </>
  );
};
